import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Map, BookOpen, User, Plus, ArrowLeft, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TextSizeToggle } from "@/components/TextSizeToggle";

const TABS = [
  { to: "/oggi", label: "Oggi", icon: Sun, id: "oggi" },
  { to: "/percorso", label: "Percorso", icon: Map, id: "percorso" },
  { to: "/coach", label: "Coach", icon: MessageCircle, id: "coach" },
  { to: "/diario", label: "Diario", icon: BookOpen, id: "diario" },
  { to: "/profilo", label: "Profilo", icon: User, id: "profilo" },
];

export function useGoBack(fallback = "/profilo") {
  const navigate = useNavigate();
  return () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(fallback));
}

function BackButton({ onClick, testid = "header-back-button" }) {
  return (
    <button onClick={onClick} data-testid={testid} aria-label="Indietro" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#D4D4D8] transition active:scale-95">
      <ArrowLeft size={18} />
    </button>
  );
}

export function AppHeader({ title, greeting, showAdd = false, onAdd }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const isAdmin = pathname.startsWith("/admin/");
  const isTab = TABS.some((t) => t.to === pathname);
  return (
    <header className="sticky top-0 z-40 border-b border-[#27272A]/40 bg-[#09090B]/80 px-5 py-3.5 backdrop-blur-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!isTab && <BackButton onClick={isAdmin ? () => navigate("/admin") : goBack} />}
          <img src="/logo-horizontal.png" alt="Coach2Go" className={`${isTab ? "h-7" : "h-6"} w-auto object-contain`} data-testid="app-logo" />
        </div>
        <div className="flex items-center gap-2">
          <TextSizeToggle />
          {showAdd && (
            <button
              onClick={onAdd}
              data-testid="header-add-button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#18181C] text-[#D4AF37] shadow-md transition active:scale-95 hover:bg-[#222228]"
            >
              <Plus size={18} />
            </button>
          )}
        </div>
      </div>
      {(greeting || title) && (
        <div className="mt-3 leading-tight">
          {greeting && <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#B4B4BC]">{greeting}</p>}
          {title && <p className="text-lg tracking-wide text-[#FAF9F6] font-semibold">{title}</p>}
        </div>
      )}
    </header>
  );
}

export function TopLogoBar({ onBack, testid, right }) {
  return (
    <div className="relative mb-4 flex h-9 items-center">
      {onBack ? <BackButton onClick={onBack} testid={testid} /> : <span className="h-9 w-9" />}
      <img
        src="/logo-horizontal.png"
        alt="Coach2Go"
        data-testid="app-logo"
        className={right ? "pointer-events-none ml-3 h-6 w-auto object-contain" : "pointer-events-none absolute left-1/2 h-6 w-auto -translate-x-1/2 object-contain"}
      />
      {right && <div className="ml-auto">{right}</div>}
    </div>
  );
}

export function BottomTabs() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <nav className="fixed bottom-0 left-1/2 z-50 flex w-full max-w-[430px] -translate-x-1/2 items-center justify-around border-t border-[#27272A]/60 bg-[#09090B]/90 px-1 py-2 backdrop-blur-xl min-[360px]:px-3">
      {TABS.map((t) => {
        const active = pathname === t.to || pathname.startsWith(t.to + "/");
        return (
          <Link
            key={t.id}
            to={t.to}
            data-testid={`nav-tab-${t.id}`}
            className="relative flex flex-col items-center gap-1 px-1.5 py-1 min-[360px]:px-3"
            style={{ color: active ? "#D4AF37" : "#8A8E96" }}
          >
            {active && (
              <span className="absolute -top-2 h-[2px] w-6 rounded-full bg-gradient-to-r from-[#E5C158] to-[#997A20] shadow-[0_0_8px_#D4AF37]" />
            )}
            <t.icon size={21} strokeWidth={active ? 2.2 : 1.8} />
            <span className="text-[12px] font-medium">{t.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function AppShell({ title, greeting, showAdd, onAdd, children }) {
  return (
    <div className="min-h-screen pb-28">
      <AppHeader title={title} greeting={greeting} showAdd={showAdd} onAdd={onAdd} />
      <main className="pb-4">{children}</main>
      <BottomTabs />
    </div>
  );
}
